/**
 * Générateur — Tester si un nombre est solution d'une équation.
 * Attendu ID : 162 | Niveau : 4ème | Domaine : Calcul littéral (O26)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O26. Critère de réussite (image page30) : « 4 est-il solution des équations :
 * 3x + 2 = 8 ; 5x − 6 = 3x + 2 ; x² − 9 = 3x − 5 ; (x−1)/12 = 1/4 ? ». Ex.3
 * (relier nombre ↔ équations), Ex.4 (quel nombre est solution), Ex.7 (erreurs
 * d'élèves sur A = 5x − 2(3x + 5), x = −3), Ex.8 (tableau de valeurs).
 *
 * Substitution EXACTE par construction (entiers relatifs, rationnels pour la
 * ligne à fraction). Aucun solveur test-side ne couvre 162 (solvers/index.js ne
 * route que les automatismes 1106 à 1111) : chaque membre est re-calculé dans
 * le gen par un second chemin (throw), et le test jetable tmp-revue-162
 * recalcule chaque membre depuis le texte affiché.
 *
 * Plan de cartes (validé 2026-07-14, historique) :
 *   tester     — « n est-il solution ? » sur plusieurs équations (V/F) + quel
 *                nombre est solution (qcm).
 *   relier     — relier chaque nombre à l'équation dont il est solution.
 *   substituer — v2 : tester si x = v est solution d'une équation en évaluant
 *                chaque membre puis leur différence (saisie) + erreurs d'élèves (qcm).
 *   tableau    — v2 : tableau de valeurs du membre gauche d'une équation, puis
 *                conclusion « la solution parmi ces nombres est x = … » (saisie).
 *
 * Revue 2026-09-23 (vague 153-163), décisions du coordinateur appliquées en
 * mode auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 4) :
 * « Il teste si un nombre est solution d'une équation. » Exemple : « 4 est-il
 * solution des équations suivantes ? 3x + 2 = 8 ; 5x − 6 = 3x + 2 ;
 * x² − 9 = 3x − 5 ; (x − 1)/12 = 1/4. » Cours : « On remplace la valeur dans
 * les deux membres de l'équation. D'une part … D'autre part … ».
 *   - KaTeX partout (règle prof) : équations, nombres testés, choix, sources et
 *     cibles du relier, lignes du V/F, cellules du tableau, phrases à trous en
 *     lignes KaTeX globales de moins de soixante caractères ; moins ASCII,
 *     coefficient 1 masqué, tête négative groupée (helpers de litteral.js).
 *   - Rappel brouillon IMPÉRATIF sur toutes les cartes (RAPPEL_BROUILLON),
 *     juste avant la question : le cours écrit les deux calculs, les cartes
 *     remplacent des nombres négatifs dans x², dans a·x − k(c·x + d).
 *   - « tester » V/F : au moins une ligne fausse de la famille principale
 *     (signe_oublie si le nombre testé est négatif : la ligne est vraie pour
 *     son opposé ; concatenation s'il est positif : 3x lu 34, modèle 1111) ;
 *     une ligne x² − b = cx + d un tableau sur trois et une ligne
 *     (x − a)/n = p/q un tableau sur quatre, les deux formes de l'exemple qui
 *     manquaient (choix de fréquence mode auto, À ARBITRER). Les fausses
 *     n'étaient que des voisins à ± 1 à 3 (le piège du signe : 0,8 %).
 *   - « tester » QCM : distracteurs relus sur l'équation ax + b = cx + d,
 *     familles oppose, signe_constante, signe_inconnue, membre_droit_sans_x,
 *     division_inversee ; entiers, distincts, différents de la solution, de
 *     valeur absolue au plus 10, trois tirés parmi ceux qui existent, nouveau
 *     tirage complet (borné) s'il y en a moins de trois. Ils étaient tirés au
 *     hasard dans ± 1 à 5. Résiduel mesuré, mode auto, À ARBITRER : oppose
 *     est la seule famille toujours entière, elle est servie dans 98 % des
 *     tirages et la clé est alors dans l'unique paire « nombre et son
 *     opposé » des choix (82 %) ; l'élève qui devine entre les deux a une
 *     chance sur deux au lieu d'une sur quatre (il doit quand même remplacer
 *     pour trancher). Parade possible, non appliquée car hors du réservoir
 *     décidé : grille oppose × signe_constante (−x0, s, −s), paire unique
 *     0 %, mais la clé tombe alors au milieu des choix triés 73 % du temps.
 *   - « relier » : TROIS nombres, CINQ équations dont deux leurres nommés
 *     (signe_oublie : l'équation d'un nombre négatif réécrite pour son opposé ;
 *     concatenation : celle d'un nombre positif pour « 3x lu 34 »), aucun
 *     nombre ne résout un leurre, « Deux équations sont en trop. » (quatre
 *     contre quatre sans leurre : la dernière paire était gratuite).
 *   - « substituer » saisie : la conclusion devient un trou (« Est-ce une
 *     solution ? » oui ou non) : écrite dans la phrase, elle donnait la
 *     réponse dans 50 % des tirages ; nouveau tirage complet quand la constante
 *     du membre de droite dépasse 40 (12,7 % des tirages) ; équations
 *     dégénérées exclues (0,5 %) ; « membre de gauche », « membre de droite » ;
 *     « Écris le signe - devant un résultat négatif. » en phrase.
 *   - « erreurs » QCM : les conclusions des élèves sont RETIRÉES (l'élève qui
 *     concluait seul était la clé dans 50,4 % des tirages) ; question « Qui a
 *     calculé juste le membre de gauche ? » ; membre de gauche aux formes de
 *     l'exemple, b + ax, ax − b ou x² − b, pour une valeur positive de 2 à 9 ;
 *     quatre élèves, prénoms tirés, valeurs deux à deux distinctes par
 *     construction. Familles attestées gauche_a_droite (DEPP 4e 2023 q.17),
 *     concatenation (q.17), double_pour_carre (Eduscol 4e 2023) ; chaque forme
 *     n'en porte qu'une ou deux, la troisième erreur est complétée par une
 *     famille nommée du parc (lecture_additive, x_ignore, carre_oublie : 1111,
 *     1228) (mode auto, À ARBITRER). Le membre de droite est l'une des quatre
 *     valeurs des élèves, tirée au hasard : il ne désigne pas la clé (mode
 *     auto, À ARBITRER).
 *   - « tableau » saisie : consigne « Calcule le membre de gauche pour chaque
 *     nombre du tableau, puis conclus. » (les cases du tableau ne se
 *     remplissaient pas) ; « celui qui est solution » (l'équation est du second
 *     degré, elle en a deux, l'autre n'est jamais parmi les trois nombres) ;
 *     parenthèses d'énoncé retirées ; nouveau tirage borné puis repli vérifié
 *     (l'ancien gardait le dernier tirage même raté).
 *   - Contrat des distracteurs exporté (REGLES_DISTRACTEURS_QCM), codes dans
 *     _meta.params.distracteurs.
 * Six formats (mêmes clés), dont deux QCM.
 *
 * ── Révision 2026-09-24 (demande du prof, validée) ────────────────────────
 *   - « Ce nombre est-il solution ? », Vrai/Faux : TROIS équations au lieu de
 *     quatre (« c'est trop long sinon » : chaque ligne demande de calculer
 *     les deux membres), une ou deux vraies ; la ligne de la famille
 *     principale reste toujours là, x² un tableau sur trois et fraction un sur
 *     quatre comme avant ; repli : les trois premières lignes de l'exemple.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, tex,
} from '../../../utils';
import { rat, texAffine, texNombre, texPoly } from './litteral';

const ATTENDU_ID = 162;

const L_TESTER = 'Ce nombre est-il solution ?';
const L_RELIER = 'Relier un nombre à son équation';
const L_SUBST = 'Calculer une expression pour une valeur';
const L_TABLEAU = 'Un tableau de valeurs';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (règle prof 2026-09-23, par attendu) : IMPÉRATIF sur les six
// cartes. Tester, c'est remplacer puis calculer chaque membre ; le cours écrit
// les deux calculs, « d'une part, d'autre part ». Forme maison du parc (gen_149).
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

// La contrainte d'écriture, en phrase, avec le « - » que l'élève tape (gen_136).
const PHRASE_SIGNE = 'Écris le signe - devant un résultat négatif.';

const VALEURS = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];
const ECARTS = [-3, -2, -1, 1, 2, 3];

// ── Rationnels (second chemin de la ligne à fraction) ──
const rAdd = (a, b) => rat(a.p * b.q + b.p * a.q, a.q * b.q);
const rEgal = (a, b) => a.p === b.p && a.q === b.q;

/** « {-4} » pour le nombre, en KaTeX. */
const nbTex = (n) => tex(texNombre(n));

// ═════════ Carte 1 — Tester (V/F et QCM) ═════════
//
// Une ligne du V/F : { forme, latex, membres(x) → [gauche, droite] en
// rationnels, code }. membres() recalcule chaque membre par substitution
// directe, indépendamment de la formule qui a choisi la constante : c'est le
// second chemin (throw si la ligne ne dit pas ce qu'on attend d'elle).

const ligneUne = (a, b, C) => ({
    forme: 'une', latex: `${texAffine(a, b)} = ${texNombre(C)}`,
    membres: (x) => [rat(a * x.p + b * x.q, x.q), rat(C)],
    concat: (v) => [10 * a + v + b, C],
});
const ligneDeux = (a, b, c, d) => ({
    forme: 'deux', latex: `${texAffine(a, b)} = ${texAffine(c, d)}`,
    membres: (x) => [rat(a * x.p + b * x.q, x.q), rat(c * x.p + d * x.q, x.q)],
    concat: (v) => [10 * a + v + b, 10 * c + v + d],
});
const ligneCarre = (b, c, d) => ({
    forme: 'carre', latex: `${texPoly([[1, 'x^2'], [-b, '']])} = ${texAffine(c, d)}`,
    membres: (x) => [rat(x.p * x.p - b * x.q * x.q, x.q * x.q), rat(c * x.p + d * x.q, x.q)],
    doublePourCarre: (v) => [2 * v - b, c * v + d],        // x² lu 2x
    carreNegatif: (v) => [-(v * v) - b, c * v + d],         // (−3)² lu −9
});
const ligneFraction = (a, n, r) => ({
    forme: 'fraction',
    latex: `\\dfrac{x - ${a}}{${n}} = ${r.p < 0 ? '-' : ''}\\dfrac{${Math.abs(r.p)}}{${r.q}}`,
    membres: (x) => [rat(x.p - a * x.q, n * x.q), r],
});

const egaux = ([g, d]) => rEgal(g, d);

/**
 * Une ligne de forme donnée pour le nombre testé v, vraie ou fausse d'une
 * famille (code) ; null si la famille ne s'applique pas à ce tirage.
 */
const tirerLigne = (v, forme, code) => {
    if (forme === 'une') {
        const a = randInt(2, 6);
        const b = randInt(1, 6) * randomChoice([1, -1]);
        const C = code === null ? a * v + b
            : code === 'signe_oublie' ? a * -v + b
                : code === 'concatenation' ? 10 * a + v + b
                    : a * v + b + randomChoice(ECARTS);
        return ligneUne(a, b, C);
    }
    if (forme === 'deux') {
        const a = randInt(2, 6);
        const c = randomChoice([2, 3, 4, 5, 6].filter((k) => k !== a));
        const b = randInt(1, 6) * randomChoice([1, -1]);
        const d = code === null ? (a - c) * v + b
            : code === 'signe_oublie' ? (a - c) * -v + b
                : code === 'concatenation' ? 10 * (a - c) + b
                    : (a - c) * v + b + randomChoice(ECARTS);
        return ligneDeux(a, b, c, d);
    }
    if (forme === 'carre') {
        const b = randInt(1, 20);
        const c = randInt(1, 5);
        if (code === 'double_pour_carre' && v === 2) return null;   // 2x = x² pour x = 2
        const d = code === null ? v * v - b - c * v
            : code === 'signe_oublie' ? v * v - b + c * v
                : code === 'double_pour_carre' ? 2 * v - b - c * v
                    : code === 'carre_negatif' ? -v * v - b - c * v
                        : v * v - b - c * v + randomChoice(ECARTS);
        return ligneCarre(b, c, d);
    }
    // fraction (x − a)/n = p/q, les deux fractions jamais entières (boucle bornée)
    for (let essai = 0; essai < 30; essai++) {
        const a = randInt(1, 9);
        const n = randInt(2, 12);
        const vraie = rat(v - a, n);
        if (v === a || vraie.q === 1) continue;
        let r = vraie;
        if (code === 'signe_oublie') r = rat(-v - a, n);
        else if (code === 'voisin') r = rAdd(vraie, rat(randomChoice([-1, 1]), vraie.q));
        if (r.q === 1 || (code !== null && rEgal(r, vraie))) continue;
        return ligneFraction(a, n, r);
    }
    return null;
};

/** Contrôle d'une ligne (second chemin) : vraie ssi code null, et l'erreur nommée la rend vraie. */
const controlerLigne = (ligne, v, code) => {
    const estVraie = egaux(ligne.membres(rat(v)));
    if (estVraie !== (code === null)) throw new Error(`gen_162 : ligne ${ligne.latex} pour ${v} (${code})`);
    const lecture = {
        signe_oublie: () => egaux(ligne.membres(rat(-v))),
        concatenation: () => { const [g, d] = ligne.concat(v); return g === d; },
        double_pour_carre: () => { const [g, d] = ligne.doublePourCarre(v); return g === d; },
        carre_negatif: () => { const [g, d] = ligne.carreNegatif(v); return g === d; },
    }[code];
    if (lecture && !lecture()) throw new Error(`gen_162 : ${code} ne rend pas vraie ${ligne.latex}`);
    return ligne;
};

// Familles des lignes fausses secondaires, par forme.
const famillesSecondaires = (forme, v) => {
    if (forme === 'carre') return [...(v !== 2 ? ['double_pour_carre'] : []), ...(v < 0 ? ['carre_negatif', 'signe_oublie'] : []), 'voisin'];
    if (forme === 'fraction') return v < 0 ? ['signe_oublie', 'voisin'] : ['voisin'];
    return [v < 0 ? 'signe_oublie' : 'concatenation', 'voisin', 'voisin'];
};

const genTesterVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const v = randomChoice(VALEURS);
        // Trois lignes (prof 2026-09-24 : quatre, c'était trop long), une ou deux vraies.
        const nbVrai = randInt(1, 2);
        const verites = shuffle([...Array(nbVrai).fill(true), ...Array(3 - nbVrai).fill(false)]);
        const principal = v < 0 ? 'signe_oublie' : 'concatenation';
        // Formes : une ligne x² un tableau sur trois, une ligne à fraction un
        // tableau sur quatre, jamais sur la ligne de la famille principale.
        const iPrincipal = verites.indexOf(false);
        const libres = shuffle([0, 1, 2].filter((i) => i !== iPrincipal));
        const formes = [0, 1, 2].map(() => randomChoice(['une', 'deux']));
        if (randInt(1, 3) === 1) formes[libres[0]] = 'carre';
        if (randInt(1, 4) === 1) formes[libres[1]] = 'fraction';
        const codes = verites.map((vraie, i) => {
            if (vraie) return null;
            if (i === iPrincipal) return principal;
            return randomChoice(famillesSecondaires(formes[i], v));
        });
        const lignes = formes.map((forme, i) => tirerLigne(v, forme, codes[i]));
        if (lignes.some((l) => !l)) continue;
        if (new Set(lignes.map((l) => l.latex)).size < 3) continue;
        lignes.forEach((l, i) => controlerLigne(l, v, codes[i]));
        const payload = makeVraiFaux(lignes.map((l) => tex(l.latex)), verites.map((vraie) => (vraie ? 0 : 1)),
            `${RAPPEL_BROUILLON}<p><b>Le nombre ${nbTex(v)} est-il solution de chaque équation ?</b></p>`,
            opts(L_TESTER));
        payload._meta.params = {
            v, formes, codes, lignes: lignes.map((l) => l.latex),
            distracteurs: codes.filter(Boolean),
        };
        return payload;
    }
    // Repli déterministe vérifié : 4 testé, l'exemple de réussite (deux vraies,
    // 5x − 6 = 3x + 2 et 3x + 2 = 14, une concaténation).
    const lignes = [ligneDeux(5, -6, 3, 2), ligneUne(3, 2, 14), ligneUne(3, 2, 36)];
    const codes = [null, null, 'concatenation'];
    lignes.forEach((l, i) => controlerLigne(l, 4, codes[i]));
    const payload = makeVraiFaux(lignes.map((l) => tex(l.latex)), [0, 0, 1],
        `${RAPPEL_BROUILLON}<p><b>Le nombre ${nbTex(4)} est-il solution de chaque équation ?</b></p>`, opts(L_TESTER));
    payload._meta.params = { v: 4, codes, lignes: lignes.map((l) => l.latex), distracteurs: ['concatenation'] };
    return payload;
};

// QCM « Quel nombre est solution ? » : ax + b = cx + d, solution entière x0.
// Le réservoir d'erreurs nommées, relues sur l'équation affichée.
const reservoirResoudre = (a, b, c, d, x0) => [
    { code: 'oppose', num: -x0, den: 1 },                  // le signe de la solution perdu
    { code: 'signe_constante', num: d + b, den: a - c },   // b passé de l'autre côté sans changer de signe
    { code: 'signe_inconnue', num: d - b, den: a + c },    // cx passé de l'autre côté sans changer de signe
    { code: 'membre_droit_sans_x', num: d - b, den: a },   // cx oublié
    { code: 'division_inversee', num: a - c, den: d - b }, // (a − c)/(d − b) : jamais entière ici (1/x0)
];

/** Valeurs entières distinctes du réservoir, ≠ x0, |v| ≤ borne, dans un ordre tiré au hasard. */
const distracteursNommes = (a, b, c, d, x0, borne) => {
    const vus = new Set([x0]);
    const out = [];
    shuffle(reservoirResoudre(a, b, c, d, x0)).forEach(({ code, num, den }) => {
        if (den === 0 || num % den !== 0) return;
        const valeur = num / den + 0;                       // + 0 : jamais « -0 »
        if (vus.has(valeur) || Math.abs(valeur) > borne) return;
        vus.add(valeur);
        out.push({ code, valeur });
    });
    return out;
};

const assemblerTesterQcm = (a, b, c, d, x0, distracteurs) => {
    const eq = `${texAffine(a, b)} = ${texAffine(c, d)}`;
    const choix = [x0, ...distracteurs.map((f) => f.valeur)].map(nbTex);
    const payload = makeQcm(choix, choix[0],
        `${RAPPEL_BROUILLON}<p><b>Quel nombre est solution de l'équation ${tex(eq)} ?</b></p>`, opts(L_TESTER));
    payload._meta.params = { a, b, c, d, x0, distracteurs: distracteurs.map((f) => f.code) };
    return payload;
};

const genTesterQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const a = randInt(2, 6);
        const c = randomChoice([1, 2, 3, 4, 5].filter((k) => k !== a));
        const x0 = randomChoice(VALEURS);
        const b = randInt(1, 6) * randomChoice([1, -1]);
        const d = (a - c) * x0 + b;
        if (a * x0 + b !== c * x0 + d) throw new Error('gen_162 : tester_qcm');   // second chemin : substitution
        const distracteurs = distracteursNommes(a, b, c, d, x0, 10);
        if (distracteurs.length < 3) continue;
        return assemblerTesterQcm(a, b, c, d, x0, distracteurs.slice(0, 3));
    }
    // Repli déterministe vérifié : 4x + 2 = 2x + 8, solution 3 ; oppose −3,
    // signe_constante (8 + 2)/2 = 5, signe_inconnue (8 − 2)/6 = 1.
    const distracteurs = distracteursNommes(4, 2, 2, 8, 3, 10);
    if (distracteurs.length < 3) throw new Error('gen_162 : repli tester_qcm');
    return assemblerTesterQcm(4, 2, 2, 8, 3, distracteurs.slice(0, 3));
};

// ═════════ Carte 2 — Relier un nombre à son équation ═════════
//
// Trois nombres, cinq équations ax + b = C : les trois bonnes et deux leurres
// nommés, écrits avec le MÊME membre de gauche que l'équation d'un des nombres :
//   · signe_oublie  : pour un nombre négatif n, C = a·|n| + b (vraie pour |n|,
//                     qui n'est jamais une source) ;
//   · concatenation : pour un nombre positif n, C = (10a + n) + b (« 3x lu 34 »,
//                     solution 10 + n/a, jamais une source).

const CONSIGNE_RELIER = `${RAPPEL_BROUILLON}<p><b>Relie chaque nombre à l'équation dont il est solution.</b> Deux équations sont en trop.</p>`;

const eqRelier = (a, b, C) => `${texAffine(a, b)} = ${texNombre(C)}`;

const assemblerRelier = (equations, leurres) => {
    const pairs = equations.map((e) => ({ source: nbTex(e.n), target: tex(eqRelier(e.a, e.b, e.C)) }));
    const cibles = leurres.map((l) => ({ target: tex(eqRelier(l.a, l.b, l.C)), code: l.code }));
    const cibleTextes = [...pairs.map((p) => p.target), ...cibles.map((l) => l.target)];
    if (new Set(cibleTextes).size !== 5) throw new Error('gen_162 : relier, cibles en double');
    // second chemin : chaque source résout SA cible et aucune autre, aucun leurre
    equations.forEach((e) => {
        if (e.a * e.n + e.b !== e.C) throw new Error('gen_162 : relier, cible fausse');
        equations.forEach((f) => { if (f !== e && f.a * e.n + f.b === f.C) throw new Error('gen_162 : relier, deux cibles'); });
        leurres.forEach((l) => { if (l.a * e.n + l.b === l.C) throw new Error('gen_162 : relier, leurre résolu'); });
    });
    const payload = makeRelier(shuffle(pairs), CONSIGNE_RELIER, { ...opts(L_RELIER), cibles_leurres: cibles });
    payload._meta.params = {
        nombres: equations.map((e) => e.n),
        distracteurs: Object.fromEntries(cibles.map((l) => [l.target, l.code])),
    };
    return payload;
};

const genRelierSolution = () => {
    for (let essai = 0; essai < 80; essai++) {
        const nombres = shuffle(VALEURS).slice(0, 3);
        const negatifs = nombres.filter((n) => n < 0 && !nombres.includes(-n));
        const positifs = nombres.filter((n) => n > 0);
        if (!negatifs.length || !positifs.length) continue;
        const equations = nombres.map((n) => {
            const a = randInt(2, 6);
            const b = randInt(1, 6) * randomChoice([1, -1]);
            return { n, a, b, C: a * n + b };
        });
        const nNeg = randomChoice(negatifs);
        const nPos = randomChoice(positifs);
        const eNeg = equations.find((e) => e.n === nNeg);
        const ePos = equations.find((e) => e.n === nPos);
        const leurres = [
            { a: eNeg.a, b: eNeg.b, C: eNeg.a * -eNeg.n + eNeg.b, code: 'signe_oublie' },
            { a: ePos.a, b: ePos.b, C: 10 * ePos.a + ePos.n + ePos.b, code: 'concatenation' },
        ];
        const textes = [...equations, ...leurres].map((e) => eqRelier(e.a, e.b, e.C));
        if (new Set(textes).size !== 5) continue;
        if (equations.some((e) => [...equations.filter((f) => f !== e), ...leurres].some((f) => f.a * e.n + f.b === f.C))) continue;
        return assemblerRelier(equations, leurres);
    }
    // Repli déterministe vérifié : −2, 3, 5 ; leurres 3x + 4 = 10 (solution 2)
    // et 2x − 1 = 22 (solution 23/2).
    return assemblerRelier(
        [{ n: -2, a: 3, b: 4, C: -2 }, { n: 3, a: 2, b: -1, C: 5 }, { n: 5, a: 4, b: 3, C: 23 }],
        [{ a: 3, b: 4, C: 10, code: 'signe_oublie' }, { a: 2, b: -1, C: 22, code: 'concatenation' }],
    );
};

// ═════════ Carte 3 — Calculer une expression (saisie + erreurs QCM) ═════════

// A = a·x − k(c·x + d)
const latexComposee = (a, k, c, d) => `${a}x - ${k}(${c}x + ${d})`;
const evalComposee = (a, k, c, d, v) => a * v - k * (c * v + d);

// La phrase à trous : trois lignes KaTeX globales (règle « tout en math ou
// rien »), les deux membres puis la conclusion, qui est un TROU (oui ou non).
const phraseSubst = (G, D, estSol) => [
    `$\\text{Membre de gauche : } {{${G}}}$`,
    `$\\text{Membre de droite : } {{${D}}}$`,
    `$\\text{Est-ce une solution ? } {{${estSol ? 'oui' : 'non'}}}$`,
].join('\n');

const assemblerSubst = (latex, v, G, D, estSol, params) => {
    const texte = `<p>On considère l'équation ${tex(latex)}.</p>`
        + RAPPEL_BROUILLON
        + `<p><b>Calcule chaque membre pour ${tex(`x = ${texNombre(v)}`)}, puis dis si ce nombre est solution.</b> `
        + `${PHRASE_SIGNE} Réponds par oui ou par non.</p>`;
    const payload = makeTexteTrou(phraseSubst(G, D, estSol), texte, { ...opts(L_SUBST), mode: 'saisie' });
    payload._meta.params = { ...params, v, estSol, G, D };
    return payload;
};

const genSubstSaisie = () => {
    for (let essai = 0; essai < 80; essai++) {
        const forme = randomChoice(['deuxAffines', 'composee']);
        const v = randomChoice(VALEURS);
        const estSol = randomChoice([true, false]);
        const ecart = estSol ? 0 : randomChoice(ECARTS);
        if (forme === 'deuxAffines') {
            const a = randInt(2, 6);
            const c = randomChoice([1, 2, 3, 4, 5].filter((k) => k !== a));   // a ≠ c : jamais dégénérée
            const b = randInt(1, 6) * randomChoice([1, -1]);
            const d = (a - c) * v + b - ecart;                 // membre de droite = G − ecart
            if (d === 0 || Math.abs(d) > 40) continue;
            const G = a * v + b;
            const D = c * v + d;
            if (G - D !== ecart) throw new Error('gen_162 : substituer');
            return assemblerSubst(`${texAffine(a, b)} = ${texAffine(c, d)}`, v, G, D, estSol, { forme, a, b, c, d });
        }
        const a = randInt(2, 7);
        const k = randInt(2, 5);
        const c = randInt(2, 5);
        const d = randInt(1, 6);
        const e = randInt(1, 5);
        if (a - k * c - e === 0) continue;                   // équation dégénérée
        const G = evalComposee(a, k, c, d, v);
        const f = G - e * v - ecart;
        if (f === 0 || Math.abs(f) > 40) continue;
        const D = e * v + f;
        // second chemin : le produit développé
        if (a * v - k * c * v - k * d !== G || G - D !== ecart) throw new Error('gen_162 : substituer composée');
        return assemblerSubst(`${latexComposee(a, k, c, d)} = ${texAffine(e, f)}`, v, G, D, estSol, { forme, a, k, c, d, e, f });
    }
    // Repli déterministe vérifié : 3x − 5 = 4x − 1 pour x = −4 (−17 et −17).
    return assemblerSubst(`${texAffine(3, -5)} = ${texAffine(4, -1)}`, -4, -17, -17, true, { forme: 'deuxAffines', a: 3, b: -5, c: 4, d: -1 });
};

// QCM « Qui a calculé juste le membre de gauche ? » : quatre élèves calculent
// le membre de gauche pour une valeur positive ; trois font une erreur nommée.
// Pas de conclusion (« est solution » / « n'est pas ») : l'élève qui concluait
// seul était la clé une fois sur deux. Le membre de droite est l'une des
// quatre valeurs, tirée au hasard : il ne désigne jamais la clé.
const PRENOMS = ['Aline', 'Bruno', 'Chloé', 'Driss', 'Emma', 'Farid', 'Inès', 'Léo', 'Nora', 'Yanis'];

const FORMES_ERREURS = {
    // b + ax : la forme du piège gauche_a_droite (DEPP 4e 2023 q.17)
    b_ax: {
        latex: (a, b) => `${b} + ${a}x`,
        juste: (a, b, v) => b + a * v,
        controle: (a, b, v) => Array(a).fill(v).reduce((s, t) => s + t, b),
        erreurs: (a, b, v) => [
            { code: 'gauche_a_droite', valeur: (b + a) * v },           // (2 + 3) × 4
            { code: 'concatenation', valeur: b + 10 * a + v },          // 3x lu 34
            { code: 'lecture_additive', valeur: b + a + v },            // 3x lu 3 + x
        ],
    },
    ax_b: {
        latex: (a, b) => `${a}x - ${b}`,
        juste: (a, b, v) => a * v - b,
        controle: (a, b, v) => Array(a).fill(v).reduce((s, t) => s + t, -b),
        erreurs: (a, b, v) => [
            { code: 'concatenation', valeur: 10 * a + v - b },
            { code: 'lecture_additive', valeur: a + v - b },
            { code: 'x_ignore', valeur: a - b },                        // le x oublié
        ],
    },
    x2_b: {
        latex: (a, b) => `x^2 - ${b}`,
        juste: (a, b, v) => v * v - b,
        controle: (a, b, v) => Array(v).fill(v).reduce((s, t) => s + t, -b),
        erreurs: (a, b, v) => [
            { code: 'double_pour_carre', valeur: 2 * v - b },           // x² lu 2x
            { code: 'carre_oublie', valeur: v - b },                    // x² lu x
            { code: 'concatenation', valeur: 10 * v + 2 - b },          // 4² lu 42
        ],
    },
};

const assemblerErreurs = (forme, a, b, v, eleves, C) => {
    const f = FORMES_ERREURS[forme];
    const cle = eleves.find((el) => el.code === null);
    const texte = `<p>On considère l'équation ${tex(`${f.latex(a, b)} = ${texNombre(C)}`)}. `
        + `Quatre élèves testent si ${tex(`x = ${v}`)} est solution. Voici ce que chacun trouve pour le membre de gauche.</p>`
        + `<p>${eleves.map((el) => `${el.nom} trouve ${nbTex(el.valeur)}.`).join(' ')}</p>`
        + RAPPEL_BROUILLON
        + '<p><b>Qui a calculé juste le membre de gauche ?</b></p>';
    const payload = makeQcm(eleves.map((el) => el.nom), cle.nom, texte, opts(L_SUBST));
    payload._meta.params = {
        forme, a, b, v, C, juste: cle.valeur,
        eleves: eleves.map(({ nom, valeur, code }) => ({ nom, valeur, code })),
        distracteurs: eleves.filter((el) => el.code).map((el) => el.code),
    };
    return payload;
};

const genErreursQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const forme = randomChoice(Object.keys(FORMES_ERREURS));
        const f = FORMES_ERREURS[forme];
        const a = randInt(2, 6);
        const b = forme === 'x2_b' ? randInt(1, 20) : randInt(1, 9);
        const v = forme === 'x2_b' ? randInt(3, 9) : randInt(2, 9);   // x² : 2x = x² pour x = 2
        const juste = f.juste(a, b, v);
        if (juste !== f.controle(a, b, v)) throw new Error('gen_162 : erreurs, second chemin');
        const erreurs = f.erreurs(a, b, v);
        const valeurs = [juste, ...erreurs.map((e) => e.valeur)];
        if (new Set(valeurs).size !== 4) continue;           // 3x lu 3 + x = 3x pour x = 2 et a = 2, etc.
        const noms = shuffle(PRENOMS).slice(0, 4);
        const eleves = shuffle([{ valeur: juste, code: null }, ...erreurs]).map((el, i) => ({ ...el, nom: noms[i] }));
        const C = randomChoice(valeurs);
        return assemblerErreurs(forme, a, b, v, eleves, C);
    }
    // Repli déterministe vérifié : 2 + 3x pour x = 4 ; 14 juste, 20, 36, 9.
    const eleves = [
        { nom: 'Aline', valeur: 14, code: null }, { nom: 'Bruno', valeur: 20, code: 'gauche_a_droite' },
        { nom: 'Chloé', valeur: 36, code: 'concatenation' }, { nom: 'Driss', valeur: 9, code: 'lecture_additive' },
    ];
    return assemblerErreurs('b_ax', 3, 2, 4, eleves, 20);
};

// ═════════ Carte 4 — Tableau de valeurs ═════════

// A = x² − b·x + c
const latexQuad = (b, c) => texPoly([[1, 'x^2'], [-b, 'x'], [c, '']]);
const evalQuad = (b, c, v) => v * v - b * v + c;

// Le tableau de valeurs sert un TEST DE SOLUTION : l'équation (membre de gauche
// quadratique = cible) est affichée, la cible étant par construction la valeur
// d'exactement UN des trois nombres (valeurs deux à deux distinctes exigées :
// x² − bx + c est symétrique autour de b/2, l'autre solution b − x n'est donc
// jamais parmi les trois). Présentation en tableau (convention de gen_933),
// les trous sont portés par la phrase.
const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

const assemblerTableau = (b, c, xs, vals, idx) => {
    const cible = vals[idx];
    const xSol = xs[idx];
    // second chemin : x(x − b) + c, et une seule des trois valeurs vaut la cible
    xs.forEach((v, i) => { if (v * (v - b) + c !== vals[i]) throw new Error('gen_162 : tableau'); });
    if (vals.filter((val) => val === cible).length !== 1) throw new Error('gen_162 : tableau, deux solutions');
    const tableau = tableEnonce(
        ligneTable(tex('x'), xs.map(nbTex)),
        ligneTable(tex(latexQuad(b, c)), xs.map(() => '…')),
    );
    const phrase = [
        ...xs.map((v, i) => `$\\text{Pour } x = ${texNombre(v)} \\text{, le membre de gauche vaut } {{${vals[i]}}}$`),
        `$\\text{Parmi ces nombres, celui qui est solution est } x = {{${xSol}}}$`,
    ].join('\n');
    const texte = `<p>On considère l'équation ${tex(`${latexQuad(b, c)} = ${texNombre(cible)}`)}.</p>`
        + `\n${tableau}\n`
        + RAPPEL_BROUILLON
        + `<p><b>Calcule le membre de gauche pour chaque nombre du tableau, puis conclus.</b> ${PHRASE_SIGNE}</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { b, c, xs, vals, idx, cible, xSol };
    return payload;
};

const genTableauSaisie = () => {
    for (let essai = 0; essai < 80; essai++) {
        const b = randInt(2, 6);
        const c = randInt(1, 8);
        const xs = shuffle(VALEURS).slice(0, 3);
        const vals = xs.map((v) => evalQuad(b, c, v));
        if (new Set(vals).size !== 3) continue;
        return assemblerTableau(b, c, xs, vals, randInt(0, 2));
    }
    // Repli déterministe vérifié : x² − 4x + 2 pour −2, 1, 5 : 14, −1, 7.
    return assemblerTableau(4, 2, [-2, 1, 5], [14, -1, 7], 0);
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE d\'élève (codes dans _meta.params.distracteurs), jamais une option inventée à l\'assemblage',
        'valeurs recalculées par un second chemin (substitution directe, produit développé ou addition répétée) : throw si la clé ne tombe pas juste',
        'toutes les options d\'un QCM par le même helper (nbTex) : une seule option en KaTeX se trahirait par la police',
    ],
    tester_qcm_unique: {
        support: 'ax + b = cx + d, a de 2 à 6, c de 1 à 5 différent de a, b de ±1 à ±6, solution entière x0 de −5 à 5 non nulle',
        familles: {
            oppose: '−x0 : le signe de la solution perdu',
            signe_constante: '(d + b)/(a − c) : b passé de l\'autre côté sans changer de signe (« chacun rentre chez soi » mal fait)',
            signe_inconnue: '(d − b)/(a + c) : cx passé de l\'autre côté sans changer de signe',
            membre_droit_sans_x: '(d − b)/a : le cx du membre de droite oublié',
            division_inversee: '(a − c)/(d − b) : la division à l\'envers ; ici toujours 1/x0, donc jamais entière hors x0 = ±1 où elle vaut la clé : présente au réservoir, jamais servie',
        },
        garantie: 'trois distracteurs tirés parmi les valeurs du réservoir ENTIÈRES, distinctes, différentes de x0, de valeur absolue au plus 10 ; nouveau tirage complet (200 essais, environ 21 % des tirages conviennent) sinon, puis repli vérifié 4x + 2 = 2x + 8',
        residuel: 'À ARBITRER : oppose servie dans 98 % des tirages, la clé est alors dans l\'unique paire x0, −x0 des choix (82 %) ; la grille oppose × signe_constante supprimerait la paire unique mais mettrait la clé au milieu des choix triés (73 %)',
    },
    erreurs_qcm_unique: {
        support: 'membre de gauche b + ax, ax − b (a de 2 à 6, b de 1 à 9) ou x² − b (b de 1 à 20) pour x de 2 à 9 (de 3 à 9 pour x²) ; quatre élèves, un juste, trois erreurs ; membre de droite = une des quatre valeurs au hasard',
        familles: {
            gauche_a_droite: 'b + ax calculé (b + a) × x (DEPP 4e 2023 q.17, 22,6 %)',
            concatenation: '3x lu 34 (q.17, 15,4 %) ; pour x², 4² lu 42 (famille de 1101)',
            lecture_additive: '3x lu 3 + x (1111, 1228)',
            x_ignore: 'ax − b calculé a − b, le x oublié (1111)',
            double_pour_carre: 'x² lu 2x (Eduscol 4e 2023, 1110, 1228)',
            carre_oublie: 'x² lu x (1228)',
        },
        par_forme: 'b + ax : gauche_a_droite, concatenation, lecture_additive ; ax − b : concatenation, lecture_additive, x_ignore ; x² − b : double_pour_carre, carre_oublie, concatenation',
        garantie: 'quatre valeurs deux à deux distinctes exigées (a = x = 2 rend lecture_additive égale à la clé ; x = 2 exclu pour x²) ; nouveau tirage sinon',
        supprimees: {
            conclusions: 'les phrases « … conclut que x = v est solution » : l\'élève qui concluait seul était la clé dans 50,4 % des tirages',
            signe_de_d: 'a·v − k(c·v − d) : algébriquement identique à « − mal distribué », et le repli « juste ± k » n\'était aucune erreur',
        },
    },
    tester_tableau_vf: 'trois lignes (prof 2026-09-24 : quatre, c\'était trop long), une ou deux vraies ; au moins une fausse de la famille principale : signe_oublie (vraie pour l\'opposé du nombre testé) s\'il est négatif, concatenation (3x lu 34) s\'il est positif ; autres fausses : double_pour_carre ou carre_negatif ((−3)² lu −9) sur la ligne x², voisin (± 1 à 3)',
    relier_relier: 'trois nombres dont un négatif d\'opposé absent et un positif ; deux leurres signe_oublie et concatenation écrits avec le membre de gauche d\'une des bonnes équations ; aucun nombre ne résout un leurre',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    tester_tableau_vf: genTesterVf,
    tester_qcm_unique: genTesterQcm,
    relier_relier: genRelierSolution,
    substituer_texte_trou_saisie: genSubstSaisie,
    erreurs_qcm_unique: genErreursQcm,
    tableau_texte_trou_saisie: genTableauSaisie,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', relier: 'Relier', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'tester', label: L_TESTER, formatsList: buildFormats('tester') },
    { id: 'relier', label: L_RELIER, formatsList: buildFormats('relier') },
    { id: 'substituer', label: L_SUBST, formatsList: [...buildFormats('substituer'), ...buildFormats('erreurs')] },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
